import React, { useEffect, useState, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Stack,
  Paper,
  ButtonGroup,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import JsFileDownloader from 'js-file-download';
import Loading from '../table-elements/loading.component';
import { manageChannelScheduleContentInfo } from '../../services/ruleset.service';
import Channels from '../form-elements/channels.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  getCurrentTimestamp,
  formatTimestamp,
} from '../../utils/datetime.util';
import Iconify from '../common/iconify.component';
import EditScheduleInfo from './edit-schedule-info.component';
import ContentAssignmentRulesets from './content-assignment-rulesets.component';
import ContentSchedulingRulesets from './content-scheduling-rulesets.component';
import { CONTENT_SCHEDULE_TYPE } from '../../config/const.config';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import tooltipGlossaryInfo from '../../utils/tooltip-info.util';
import {
  exportChannelVideosData,
  exportAllChannelsSchedule,
  downloadAllChannelsSchedule,
  exportChannelVideosSkippedContentData,
  exportAllBlockedVideos,
} from '../../services/apps.service';
import OverviewRulesets from './overview-rulesets.component';
import NewChannelRelease from './new-channel-release.component';
import ContentScheduleReports from './content-schedule-reports.component';
import ValidateSetup from './validate-setup.component';
import useAccess from '../../hooks/access.hook';
import BlackoutChannels from './blackout-channels.component';
import NewChannelSchedule from './new-channel-schedule.component';

const ContentScheduleInfo = () => {
  const FILTERS_KEY = 'schedule-content-channel';

  const { hasOperationAccess, hasAdminAccess } = useAccess();

  const selectedChannel = useRef(null);
  const channelAssignmentScheduleInfo = useRef(null);
  const anchorRef = useRef(null);
  const exportStatusCheckTimer = useRef(null);
  const exportScheduleStatusCheckTimer = useRef(null);
  const exportHeroPlaylistScheduleStatusCheckTimer = useRef(null);

  const [activeScheduleType, setActiveScheduleType] = useState(
    CONTENT_SCHEDULE_TYPE.ASSIGNMENT
  );
  const [loading, setLoading] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [showScheduleInfoDialog, setShowScheduleInfoDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showAllRulesetsOverview, setShowAllRulesetsOverview] = useState(false);
  const [showNewChannelReleaseDialog, setShowNewChannelReleaseDialog] =
    useState(false);
  const [showNewChannelScheduleDialog, setShowNewChannelScheduleDialog] =
    useState(false);
  const [showValidateSetup, setShowValidateSetup] = useState(false);
  const [showBlackoutChannels, setShowBlackoutChannels] = useState(false);

  const refreshDataWithoutLoading = () => {
    manageChannelScheduleContentInfo(selectedChannel.current.id, {
      type: 'GET',
    })
      .then((res) => {
        channelAssignmentScheduleInfo.current = { ...res.data };

        channelAssignmentScheduleInfo.current.totalAvailabeVideos =
          channelAssignmentScheduleInfo.current.totalVideos -
          channelAssignmentScheduleInfo.current.alwaysPublished;

        channelAssignmentScheduleInfo.current.totalAvailabeMovies =
          channelAssignmentScheduleInfo.current.totalMovies -
          channelAssignmentScheduleInfo.current.alwaysPublishedMovies;

        channelAssignmentScheduleInfo.current.totalAvailabeEpisodes =
          channelAssignmentScheduleInfo.current.totalEpisodes -
          channelAssignmentScheduleInfo.current.alwaysPublishedEpisodes;

        setRefreshCounter((prev) => prev + 1);

        if (exportStatusCheckTimer.current) {
          clearTimeout(exportStatusCheckTimer.current);
        }
        if (
          !channelAssignmentScheduleInfo.current.exportInfo.allowToCreateNew
        ) {
          exportStatusCheckTimer.current = setTimeout(
            refreshDataWithoutLoading,
            20000
          );
        }

        if (exportScheduleStatusCheckTimer.current) {
          clearTimeout(exportScheduleStatusCheckTimer.current);
        }
        if (
          !channelAssignmentScheduleInfo.current
            .exportScheduleSkippedContentInfo.allowToCreateNew
        ) {
          exportScheduleStatusCheckTimer.current = setTimeout(
            refreshDataWithoutLoading,
            20000
          );
        }

        if (exportHeroPlaylistScheduleStatusCheckTimer.current) {
          clearTimeout(exportHeroPlaylistScheduleStatusCheckTimer.current);
        }
        if (
          !channelAssignmentScheduleInfo.current
            .exportHeroPlaylistScheduleSkippedContentInfo.allowToCreateNew
        ) {
          exportHeroPlaylistScheduleStatusCheckTimer.current = setTimeout(
            refreshDataWithoutLoading,
            20000
          );
        }
      })
      .catch(() => {
        // Ignore
      });
  };

  const handleActionSuccess = (message = '') => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (message) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message,
        });
      }, 10);
    }

    if (showNewChannelReleaseDialog) {
      setShowNewChannelReleaseDialog(false);
      refreshDataWithoutLoading();
    } else if (showNewChannelScheduleDialog) {
      setShowNewChannelScheduleDialog(false);
      setReloadCounter((prev) => prev + 1);
    }
  };

  const handleDownloadFile = (skippedContentType = null) => {
    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    let downloadFileName =
      channelAssignmentScheduleInfo.current.exportInfo.fileName;
    if (skippedContentType === CONTENT_SCHEDULE_TYPE.GENERAL) {
      downloadFileName =
        channelAssignmentScheduleInfo.current.exportScheduleSkippedContentInfo
          .fileName;
    } else if (skippedContentType === CONTENT_SCHEDULE_TYPE.HERO) {
      downloadFileName =
        channelAssignmentScheduleInfo.current
          .exportHeroPlaylistScheduleSkippedContentInfo.fileName;
    }

    let params = '';
    if (skippedContentType) {
      params = `?skippedContentType=${skippedContentType}`;
    }
    downloadAllChannelsSchedule(params)
      .then((res) => {
        setProcessing(false);
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, downloadFileName);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const hanndleAllChannelsExport = (skippedContentType = null) => {
    if (
      !skippedContentType &&
      !channelAssignmentScheduleInfo.current.exportInfo.allowToCreateNew
    ) {
      return;
    }
    if (
      skippedContentType === CONTENT_SCHEDULE_TYPE.GENERAL &&
      !channelAssignmentScheduleInfo.current.exportScheduleSkippedContentInfo
        .allowToCreateNew
    ) {
      return;
    }
    if (
      skippedContentType === CONTENT_SCHEDULE_TYPE.HERO &&
      !channelAssignmentScheduleInfo.current
        .exportHeroPlaylistScheduleSkippedContentInfo.allowToCreateNew
    ) {
      return;
    }

    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    let params = '';
    if (skippedContentType) {
      params = `?skippedContentType=${skippedContentType}`;
    }
    exportAllChannelsSchedule(params)
      .then(() => {
        setProcessing(false);
        refreshDataWithoutLoading();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const handleExportData = (forChannel = 0) => {
    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    exportChannelVideosData(forChannel)
      .then((res) => {
        setProcessing(false);
        const currentTime = getCurrentTimestamp();
        const fileName = `${selectedChannel.current.code}_assignment_${currentTime}.csv`;
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, fileName);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const handleSkippedContentExportData = (channelId, type) => {
    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    let params = '';
    if (type) {
      params = `?type=${type}`;
    }
    exportChannelVideosSkippedContentData(channelId, params)
      .then((res) => {
        setProcessing(false);
        const currentTime = getCurrentTimestamp();
        const fileName =
          type === CONTENT_SCHEDULE_TYPE.GENERAL
            ? `${selectedChannel.current.code}_schedule_skipped_content_${currentTime}.csv`
            : `${selectedChannel.current.code}_hero_playlist_schedule_skipped_content_${currentTime}.csv`;
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, fileName);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const handleSaveScheduleInfo = (data) => {
    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const payload = {
      targetMovies: data.targetMovies,
      targetEpisodes: data.targetEpisodes,
      type: 'SAVE',
    };
    manageChannelScheduleContentInfo(selectedChannel.current.id, payload)
      .then((res) => {
        channelAssignmentScheduleInfo.current = { ...res.data };

        setSnackbarInfo({
          show: true,
          type: 'success',
          message: 'Channel content target settings updated.',
        });
        setProcessing(false);
        setShowScheduleInfoDialog(false);
      })
      .catch(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Something went wrong, please try again.',
        });
        setProcessing(false);
      });
  };

  const handleExportBlockedVideos = () => {
    setProcessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    exportAllBlockedVideos()
      .then((res) => {
        setProcessing(false);
        const currentTime = getCurrentTimestamp();
        const fileName = `blocked-videos-${currentTime}.csv`;
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, fileName);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const handleChannelSelectionChange = (c) => {
    selectedChannel.current = c || null;
    channelAssignmentScheduleInfo.current = null;

    setLocalFilterValue(FILTERS_KEY, c || {});
    setReloadCounter((prev) => prev + 1);
  };

  const onRefreshCountsView = () => {
    refreshDataWithoutLoading();
  };

  useEffect(() => {
    let channelId = null;
    const localFilters = getLocalFilterValue(FILTERS_KEY);
    if (localFilters && Object.keys(localFilters).length > 0) {
      selectedChannel.current = localFilters;
      channelId = selectedChannel.current.id;
    }

    if (!channelId) {
      setLoading(false);
    }

    if (channelId) {
      setLoading(true);
      setSnackbarInfo({ ...snackbarInfo, show: false });

      manageChannelScheduleContentInfo(channelId, { type: 'GET' })
        .then((res) => {
          channelAssignmentScheduleInfo.current = { ...res.data };

          channelAssignmentScheduleInfo.current.totalAvailabeVideos =
            channelAssignmentScheduleInfo.current.totalVideos -
            channelAssignmentScheduleInfo.current.alwaysPublished;

          channelAssignmentScheduleInfo.current.totalAvailabeMovies =
            channelAssignmentScheduleInfo.current.totalMovies -
            channelAssignmentScheduleInfo.current.alwaysPublishedMovies;

          channelAssignmentScheduleInfo.current.totalAvailabeEpisodes =
            channelAssignmentScheduleInfo.current.totalEpisodes -
            channelAssignmentScheduleInfo.current.alwaysPublishedEpisodes;

          setLoading(false);

          if (exportStatusCheckTimer.current) {
            clearTimeout(exportStatusCheckTimer.current);
          }
          if (
            !channelAssignmentScheduleInfo.current.exportInfo.allowToCreateNew
          ) {
            exportStatusCheckTimer.current = setTimeout(
              refreshDataWithoutLoading,
              20000
            );
          }

          if (exportScheduleStatusCheckTimer.current) {
            clearTimeout(exportScheduleStatusCheckTimer.current);
          }
          if (
            !channelAssignmentScheduleInfo.current
              .exportScheduleSkippedContentInfo.allowToCreateNew
          ) {
            exportScheduleStatusCheckTimer.current = setTimeout(
              refreshDataWithoutLoading,
              20000
            );
          }

          if (exportHeroPlaylistScheduleStatusCheckTimer.current) {
            clearTimeout(exportHeroPlaylistScheduleStatusCheckTimer.current);
          }
          if (
            !channelAssignmentScheduleInfo.current
              .exportHeroPlaylistScheduleSkippedContentInfo.allowToCreateNew
          ) {
            exportHeroPlaylistScheduleStatusCheckTimer.current = setTimeout(
              refreshDataWithoutLoading,
              20000
            );
          }
        })
        .catch(() => {
          setSnackbarInfo({
            show: true,
            type: 'error',
            message: 'Something went wrong, please try again.',
          });
        });
    }

    return () => {
      if (exportStatusCheckTimer.current) {
        clearTimeout(exportStatusCheckTimer.current);
      }
      if (exportScheduleStatusCheckTimer.current) {
        clearTimeout(exportScheduleStatusCheckTimer.current);
      }
      if (exportHeroPlaylistScheduleStatusCheckTimer.current) {
        clearTimeout(exportHeroPlaylistScheduleStatusCheckTimer.current);
      }
    };
  }, [reloadCounter]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        width="100%"
        sx={{ minHeight: '140px' }}
      >
        <Box sx={{ width: '350px', mt: 1 }}>
          <Channels
            id="channel"
            name="channel"
            label="Select Channel"
            defaultValue={selectedChannel.current}
            onChange={(c) => {
              handleChannelSelectionChange(c);
            }}
            sx={{ width: '90%' }}
            disabled={loading || processing}
          />

          {!loading && !selectedChannel.current?.id && (
            <Typography variant="body2" sx={{ mt: 2, color: 'grey' }}>
              Please select a channel to manage assignment/schedule
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: 1 }}>
          {!loading && channelAssignmentScheduleInfo.current && (
            <Box display="flex">
              <Box width={160}>
                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.totalVideos}
                    arrow
                    placement="left-start"
                  >
                    Total Videos:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.totalVideos}
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.totalMovies}
                    arrow
                    placement="left-start"
                  >
                    Total Movies:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.totalMovies}
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.totalEpisodes}
                    arrow
                    placement="left-start"
                  >
                    Total Episodes:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.totalEpisodes}
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.totalSeries}
                    arrow
                    placement="left-start"
                  >
                    Total Series:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.totalSeries}
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.totalSeasons}
                    arrow
                    placement="left-start"
                  >
                    Total Seasons:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.totalSeasons}
                    </strong>
                  </Tooltip>
                </Typography>
              </Box>
              <Box width={250}>
                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.alwaysScheduledVideos}
                    arrow
                    placement="left-start"
                  >
                    Always Scheduled Videos:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.alwaysPublished}
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.alwaysScheduledMovies}
                    arrow
                    placement="left-start"
                  >
                    Always Scheduled Movies:{' '}
                    <strong>
                      {
                        channelAssignmentScheduleInfo.current
                          .alwaysPublishedMovies
                      }
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.alwaysScheduledEpisodes}
                    arrow
                    placement="left-start"
                  >
                    Always Scheduled Episodes:{' '}
                    <strong>
                      {
                        channelAssignmentScheduleInfo.current
                          .alwaysPublishedEpisodes
                      }
                    </strong>
                  </Tooltip>
                </Typography>

                {channelAssignmentScheduleInfo.current.blockedVideos
                  .alwaysPublishedMovies > 0 && (
                  <Typography
                    variant="body2"
                    color="error"
                    mb={1}
                    sx={{ fontSize: 12 }}
                  >
                    <Tooltip
                      title={
                        tooltipGlossaryInfo.info.blockedVideos
                          .alwaysScheduledMovies
                      }
                      arrow
                      placement="left-start"
                    >
                      Always Scheduled Movies (Blocked):{' '}
                      <strong>
                        {
                          channelAssignmentScheduleInfo.current.blockedVideos
                            .alwaysPublishedMovies
                        }
                      </strong>
                    </Tooltip>
                  </Typography>
                )}
                {channelAssignmentScheduleInfo.current.blockedVideos
                  .alwaysPublishedEpisodes > 0 && (
                  <Typography
                    variant="body2"
                    color="error"
                    mb={1}
                    sx={{ fontSize: 12 }}
                  >
                    <Tooltip
                      title={
                        tooltipGlossaryInfo.info.blockedVideos
                          .alwaysScheduledEpisodes
                      }
                      arrow
                      placement="left-start"
                    >
                      Always Scheduled Episodes (Blocked):{' '}
                      <strong>
                        {
                          channelAssignmentScheduleInfo.current.blockedVideos
                            .alwaysPublishedEpisodes
                        }
                      </strong>
                    </Tooltip>
                  </Typography>
                )}
              </Box>
              <Box width={260}>
                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={
                      tooltipGlossaryInfo.info.totalVideosAvailableForScheduling
                    }
                    arrow
                    placement="left-start"
                  >
                    Videos Available For Scheduling:{' '}
                    <strong>
                      {
                        channelAssignmentScheduleInfo.current
                          .totalAvailabeVideos
                      }
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={
                      tooltipGlossaryInfo.info.totalMoviesAvailableForScheduling
                    }
                    arrow
                    placement="left-start"
                  >
                    Movies Available For Scheduling:{' '}
                    <strong>
                      {
                        channelAssignmentScheduleInfo.current
                          .totalAvailabeMovies
                      }
                    </strong>
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={
                      tooltipGlossaryInfo.info
                        .totalEpisodesAvailableForScheduling
                    }
                    arrow
                    placement="left-start"
                  >
                    Episodes Available For Scheduling:{' '}
                    <strong>
                      {
                        channelAssignmentScheduleInfo.current
                          .totalAvailabeEpisodes
                      }
                    </strong>
                  </Tooltip>
                </Typography>

                {channelAssignmentScheduleInfo.current.blockedVideos
                  .totalMovies > 0 && (
                  <Typography
                    variant="body2"
                    color="error"
                    mb={1}
                    sx={{ fontSize: 12 }}
                  >
                    <Tooltip
                      title={tooltipGlossaryInfo.info.blockedVideos.totalMovies}
                      arrow
                      placement="left-start"
                    >
                      Total Movies Scheduling (Blocked):{' '}
                      <strong>
                        {
                          channelAssignmentScheduleInfo.current.blockedVideos
                            .totalMovies
                        }
                      </strong>
                    </Tooltip>
                  </Typography>
                )}
                {channelAssignmentScheduleInfo.current.blockedVideos
                  .totalEpisodes > 0 && (
                  <Typography
                    variant="body2"
                    color="error"
                    mb={1}
                    sx={{ fontSize: 12 }}
                  >
                    <Tooltip
                      title={
                        tooltipGlossaryInfo.info.blockedVideos.totalEpisodes
                      }
                      arrow
                      placement="left-start"
                    >
                      Total Episodes Scheduling (Blocked):{' '}
                      <strong>
                        {
                          channelAssignmentScheduleInfo.current.blockedVideos
                            .totalEpisodes
                        }
                      </strong>
                    </Tooltip>
                  </Typography>
                )}
              </Box>
              <Box width={220} align="right">
                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.targetMovies}
                    arrow
                    placement="left-start"
                  >
                    Target # Movies:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.targetMovies || 0}
                    </strong>
                    {hasOperationAccess('rule_based_scheduling', 'manage') && (
                      <IconButton
                        aria-label="edit"
                        onClick={() => setShowScheduleInfoDialog(true)}
                        size="small"
                        disabled={loading || processing}
                        sx={{ py: 0, pl: 1.5 }}
                      >
                        <Iconify icon="bx:edit" />
                      </IconButton>
                    )}
                  </Tooltip>
                </Typography>

                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  <Tooltip
                    title={tooltipGlossaryInfo.info.targetEpisodes}
                    arrow
                    placement="left-start"
                  >
                    Target # Episodes:{' '}
                    <strong>
                      {channelAssignmentScheduleInfo.current.targetEpisodes ||
                        0}
                    </strong>
                    {hasOperationAccess('rule_based_scheduling', 'manage') && (
                      <IconButton
                        aria-label="edit"
                        onClick={() => setShowScheduleInfoDialog(true)}
                        size="small"
                        disabled={loading || processing}
                        sx={{ py: 0, pl: 1.5 }}
                      >
                        <Iconify icon="bx:edit" />
                      </IconButton>
                    )}
                  </Tooltip>
                </Typography>

                {!loading && selectedChannel.current?.id && (
                  <Box sx={{ mt: 2 }}>
                    <ButtonGroup
                      variant="contained"
                      ref={anchorRef}
                      aria-label="channel-schedule"
                      size="small"
                      disabled={processing}
                      sx={{ width: '100%', display: 'flex' }}
                    >
                      <Button
                        onClick={() => setShowExportOptions(!showExportOptions)}
                        sx={{ flexGrow: 1 }}
                      >
                        Export Data Options
                      </Button>
                      <Button
                        aria-controls="drop-down-icon"
                        aria-expanded
                        aria-label="drop-down-ico"
                        aria-haspopup="menu"
                        onClick={() => setShowExportOptions(!showExportOptions)}
                      >
                        <ArrowDropDownIcon />
                      </Button>
                    </ButtonGroup>
                    <Popper
                      open={showExportOptions}
                      anchorEl={anchorRef.current}
                      transition
                      disablePortal
                      sx={{ zIndex: 5 }}
                    >
                      {({ TransitionProps, placement }) => (
                        <Grow
                          {...TransitionProps}
                          style={{
                            transformOrigin:
                              placement === 'bottom'
                                ? 'right top'
                                : 'right bottom',
                          }}
                        >
                          <Paper elevation={3}>
                            <ClickAwayListener
                              onClickAway={() => setShowExportOptions(false)}
                            >
                              <MenuList
                                id="channel-schedule-button-menu"
                                autoFocusItem
                              >
                                <MenuItem
                                  disabled={loading || processing}
                                  onClick={() =>
                                    handleExportData(selectedChannel.current.id)
                                  }
                                >
                                  <Typography variant="body2">
                                    Export{' '}
                                    {selectedChannel.current?.code || 'This'}{' '}
                                    Channel Data
                                  </Typography>
                                </MenuItem>
                                {hasAdminAccess() &&
                                  channelAssignmentScheduleInfo.current
                                    .exportInfo.allowToCreateNew && (
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() => hanndleAllChannelsExport()}
                                    >
                                      <Typography variant="body2">
                                        Export All Channels Data
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() &&
                                  !channelAssignmentScheduleInfo.current
                                    .exportInfo.allowToCreateNew && (
                                    <MenuItem disabled>
                                      <Iconify
                                        icon="line-md:loading-loop"
                                        sx={{ mr: 1 }}
                                      />
                                      <Typography variant="body2">
                                        Export In Progress
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() &&
                                  channelAssignmentScheduleInfo.current
                                    .exportInfo.allowLastEntryDownload && (
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() => handleDownloadFile()}
                                    >
                                      <Typography variant="body2">
                                        Saved Export:{' '}
                                        {formatTimestamp(
                                          Number(
                                            channelAssignmentScheduleInfo
                                              .current.exportInfo.lastEntryFile
                                          ),
                                          'lll'
                                        )}
                                      </Typography>
                                    </MenuItem>
                                  )}
                                <Divider />
                                <MenuItem
                                  disabled={loading || processing}
                                  onClick={() =>
                                    handleSkippedContentExportData(
                                      selectedChannel.current.id,
                                      CONTENT_SCHEDULE_TYPE.GENERAL
                                    )
                                  }
                                >
                                  <Typography variant="body2">
                                    Export{' '}
                                    {selectedChannel.current?.code || 'This'}{' '}
                                    Channel Schedule Skipped Content Data
                                  </Typography>
                                </MenuItem>
                                {hasAdminAccess() &&
                                  channelAssignmentScheduleInfo.current
                                    .exportScheduleSkippedContentInfo
                                    .allowToCreateNew && (
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() =>
                                        hanndleAllChannelsExport(
                                          CONTENT_SCHEDULE_TYPE.GENERAL
                                        )
                                      }
                                    >
                                      <Typography variant="body2">
                                        Export All Channels Schedule Skipped
                                        Content Data
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() &&
                                  !channelAssignmentScheduleInfo.current
                                    .exportScheduleSkippedContentInfo
                                    .allowToCreateNew && (
                                    <MenuItem disabled>
                                      <Iconify
                                        icon="line-md:loading-loop"
                                        sx={{ mr: 1 }}
                                      />
                                      <Typography variant="body2">
                                        Export In Progress
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() &&
                                  channelAssignmentScheduleInfo.current
                                    .exportScheduleSkippedContentInfo
                                    .allowLastEntryDownload && (
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() =>
                                        handleDownloadFile(
                                          CONTENT_SCHEDULE_TYPE.GENERAL
                                        )
                                      }
                                    >
                                      <Typography variant="body2">
                                        Saved Schedule Export:{' '}
                                        {formatTimestamp(
                                          Number(
                                            channelAssignmentScheduleInfo
                                              .current
                                              .exportScheduleSkippedContentInfo
                                              .lastEntryFile
                                          ),
                                          'lll'
                                        )}
                                      </Typography>
                                    </MenuItem>
                                  )}
                                <Divider />
                                <MenuItem
                                  disabled={loading || processing}
                                  onClick={() =>
                                    handleSkippedContentExportData(
                                      selectedChannel.current.id,
                                      CONTENT_SCHEDULE_TYPE.HERO
                                    )
                                  }
                                >
                                  <Typography variant="body2">
                                    Export{' '}
                                    {selectedChannel.current?.code || 'This'}{' '}
                                    Channel Hero Playlist Schedule Skipped
                                    Content Data
                                  </Typography>
                                </MenuItem>
                                {hasAdminAccess() &&
                                  channelAssignmentScheduleInfo.current
                                    .exportHeroPlaylistScheduleSkippedContentInfo
                                    .allowToCreateNew && (
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() =>
                                        hanndleAllChannelsExport(
                                          CONTENT_SCHEDULE_TYPE.HERO
                                        )
                                      }
                                    >
                                      <Typography variant="body2">
                                        Export All Channels Hero Playlist
                                        Schedule Skipped Content Data
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() &&
                                  !channelAssignmentScheduleInfo.current
                                    .exportHeroPlaylistScheduleSkippedContentInfo
                                    .allowToCreateNew && (
                                    <MenuItem disabled>
                                      <Iconify
                                        icon="line-md:loading-loop"
                                        sx={{ mr: 1 }}
                                      />
                                      <Typography variant="body2">
                                        Export In Progress
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() &&
                                  channelAssignmentScheduleInfo.current
                                    .exportHeroPlaylistScheduleSkippedContentInfo
                                    .allowLastEntryDownload && (
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() =>
                                        handleDownloadFile(
                                          CONTENT_SCHEDULE_TYPE.HERO
                                        )
                                      }
                                    >
                                      <Typography variant="body2">
                                        Saved Hero Playlist Schedule Export:{' '}
                                        {formatTimestamp(
                                          Number(
                                            channelAssignmentScheduleInfo
                                              .current
                                              .exportHeroPlaylistScheduleSkippedContentInfo
                                              .lastEntryFile
                                          ),
                                          'lll'
                                        )}
                                      </Typography>
                                    </MenuItem>
                                  )}
                                {hasAdminAccess() && (
                                  <>
                                    <Divider />
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() => {
                                        setShowExportOptions(false);
                                        setShowAllRulesetsOverview(true);
                                      }}
                                    >
                                      <Typography variant="body2">
                                        Overview of All Rulesets
                                      </Typography>
                                    </MenuItem>
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() => {
                                        setShowExportOptions(false);
                                        setShowValidateSetup(true);
                                      }}
                                    >
                                      <Typography variant="body2">
                                        Validate Setup
                                      </Typography>
                                    </MenuItem>
                                    <MenuItem
                                      disabled={loading || processing}
                                      onClick={() => {
                                        setShowExportOptions(false);
                                        setShowBlackoutChannels(true);
                                      }}
                                    >
                                      <Typography variant="body2">
                                        Blackout Channels (New Release Channels)
                                      </Typography>
                                    </MenuItem>
                                    {channelAssignmentScheduleInfo.current
                                      .hasBlockedVideos && (
                                      <MenuItem
                                        disabled={loading || processing}
                                        onClick={() => {
                                          handleExportBlockedVideos();
                                        }}
                                      >
                                        <Typography variant="body2">
                                          Download Blocked Videos (All New
                                          Release Channels)
                                        </Typography>
                                      </MenuItem>
                                    )}
                                  </>
                                )}
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                )}

                {!loading &&
                  selectedChannel.current?.id &&
                  hasOperationAccess('rule_based_scheduling', 'manage') && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        color={
                          !channelAssignmentScheduleInfo.current
                            .newChannelReleaseInfo
                            ? 'secondary'
                            : 'info'
                        }
                        variant="contained"
                        aria-label="channel-schedule"
                        disabled={processing}
                        width="100%"
                        disableElevation
                        sx={{ width: '100%' }}
                        onClick={() => setShowNewChannelReleaseDialog(true)}
                      >
                        {!channelAssignmentScheduleInfo.current
                          .newChannelReleaseInfo
                          ? 'Set As New Channel Release'
                          : 'Modify New Channel Release'}
                      </Button>
                    </Box>
                  )}
                {!loading &&
                  selectedChannel.current?.id &&
                  hasOperationAccess('rule_based_scheduling', 'manage') && (
                    <Button
                      color="secondary"
                      variant="contained"
                      aria-label="new-channel-schedule"
                      disabled={processing}
                      width="100%"
                      disableElevation
                      onClick={() => setShowNewChannelScheduleDialog(true)}
                      sx={{ width: '100%', mt: 1 }}
                    >
                      Shift Schedule Earlier
                    </Button>
                  )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {loading && <Loading />}

      {!loading &&
        selectedChannel.current?.id &&
        channelAssignmentScheduleInfo.current && (
          <>
            <Box mt={3}>
              <ContentScheduleReports
                channelId={selectedChannel.current.id}
                reloadCounter={reloadCounter}
                refreshCounter={refreshCounter}
              />
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 4 }}
            >
              <Tabs
                value={activeScheduleType}
                aria-label="tabs"
                indicatorColor="primary"
                onChange={(_, newValue) => {
                  setActiveScheduleType(newValue);
                }}
              >
                <Tab
                  value={CONTENT_SCHEDULE_TYPE.ASSIGNMENT}
                  label="Assign Content"
                />
                <Tab
                  value={CONTENT_SCHEDULE_TYPE.GENERAL}
                  label="Schedule Channel"
                />
                <Tab
                  value={CONTENT_SCHEDULE_TYPE.HERO}
                  label="Schedule Hero Playlist"
                />
              </Tabs>
            </Stack>

            {activeScheduleType === CONTENT_SCHEDULE_TYPE.ASSIGNMENT && (
              <ContentAssignmentRulesets
                channelId={selectedChannel.current.id}
                refreshCountsView={onRefreshCountsView}
              />
            )}

            {activeScheduleType === CONTENT_SCHEDULE_TYPE.GENERAL && (
              <ContentSchedulingRulesets
                channelId={selectedChannel.current.id}
                activeScheduleType={activeScheduleType}
                refreshCountsView={onRefreshCountsView}
              />
            )}

            {activeScheduleType === CONTENT_SCHEDULE_TYPE.HERO && (
              <ContentSchedulingRulesets
                channelId={selectedChannel.current.id}
                activeScheduleType={activeScheduleType}
                refreshCountsView={onRefreshCountsView}
              />
            )}
          </>
        )}

      {showScheduleInfoDialog && (
        <EditScheduleInfo
          title={`Edit Schedule Info - ${selectedChannel.current.title}`}
          onCancel={() => setShowScheduleInfoDialog(false)}
          onSuccess={handleSaveScheduleInfo}
          defaultValues={channelAssignmentScheduleInfo.current}
          formSubmitted={processing}
        />
      )}

      {showAllRulesetsOverview && (
        <OverviewRulesets
          title="Overview of All Rulesets"
          onCancel={() => setShowAllRulesetsOverview(false)}
          formSubmitted={processing}
        />
      )}

      {showValidateSetup && (
        <ValidateSetup onCancel={() => setShowValidateSetup(false)} />
      )}

      {showBlackoutChannels && (
        <BlackoutChannels
          title="Blackout Channels (New Release Channels)"
          onCancel={() => setShowBlackoutChannels(false)}
        />
      )}

      {showNewChannelReleaseDialog && (
        <NewChannelRelease
          title={`${
            !channelAssignmentScheduleInfo.current.newChannelReleaseInfo
              ? 'Set As'
              : 'Modify'
          } New Channel Release - ${selectedChannel.current.code}`}
          channelId={selectedChannel.current.id}
          onCancel={() => setShowNewChannelReleaseDialog(false)}
          onSuccess={handleActionSuccess}
          newChannelReleaseInfo={
            channelAssignmentScheduleInfo.current.newChannelReleaseInfo
          }
        />
      )}

      {showNewChannelScheduleDialog && (
        <NewChannelSchedule
          title={`Shift Schedule Earlier - ${selectedChannel.current.code}`}
          channelId={selectedChannel.current.id}
          onCancel={() => setShowNewChannelScheduleDialog(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

export default ContentScheduleInfo;
